import React, { useEffect, useRef, useState, useContext } from 'react';
import { oneOf } from 'prop-types';
import classNames from 'classnames/bind';

import { extend } from '@thd-nucleus/data-sources';
import { debounce } from '@thd-olt-functional/utils';
import { ExperienceContext } from '@thd-nucleus/experience-context';

import { SpecialBuyHeader } from './SpecialBuyHeader/SpecialBuyHeader';
import { useSearchProducts } from '../hooks/useSearchProducts';
import { SpecialBuyCarousel } from './SpecialBuyCarousel/SpecialBuyCarousel';
import { SpecialBuyCategories } from './SpecialBuyCategories/SpecialBuyCategories';
import { SpecialBuyNavButton } from './SpecialBuyNavButton/SpecialBuyNavButton';
import { SpecialBuyProvider } from '../context/SpecialBuyProvider';
import styles from './special-buy-banner.module.scss';

const cNames = classNames.bind(styles);

// How large is the component when layouts shift.
const condensedLayoutBreakpoint = 750;

const SpecialBuyBanner = ({ specialBuyType }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('special-buy-banner.ready'); }, []);
  const { channel } = useContext(ExperienceContext);
  const [isCondensedLayout, setIsCondensedLayout] = useState(channel === 'mobile');
  const [bannerWidth, setBannerWidth] = useState(0);
  const bannerRef = useRef(null);

  useEffect(() => {
    if (bannerRef.current && typeof bannerRef.current === 'object' && typeof window !== 'undefined') {
      const calculateAndSetIsCondensedLayout = () => {
        const width = bannerRef.current.getBoundingClientRect()?.width;
        if (width && channel !== 'mobile') {
          setBannerWidth(width);
          setIsCondensedLayout(width < condensedLayoutBreakpoint);
        } else {
          setIsCondensedLayout(true);
        }
      };
      calculateAndSetIsCondensedLayout();
      const handleResize = debounce(50, calculateAndSetIsCondensedLayout);
      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
      };
    }

    return null;
  }, [setBannerWidth, channel]);

  let bannerId = '';
  if (specialBuyType === 'sbotd') bannerId = 'sbotdBanner';
  if (specialBuyType === 'decor') bannerId = 'sbotwDecorBanner';
  if (specialBuyType === 'pro') bannerId = 'sbotwProBanner';

  const specialBuyBannerClassNames = cNames('special-buy-banner', {
    'special-buy-banner--condensed': isCondensedLayout,
  });

  return (
    // TODO: take isCondensedLayout out of the provider
    <SpecialBuyProvider specialBuyType={specialBuyType} isCondensedLayout={isCondensedLayout}>
      <article className={specialBuyBannerClassNames} id={bannerId} ref={bannerRef}>
        <SpecialBuyHeader />
        <SpecialBuyCategories />
        {!isCondensedLayout
          ? <SpecialBuyCarousel bannerWidth={bannerWidth} />
          : <SpecialBuyNavButton isFullWidth />}
      </article>
    </SpecialBuyProvider>
  );
};

SpecialBuyBanner.dataModel = extend({}, useSearchProducts);

SpecialBuyBanner.displayName = 'SpecialBuyBanner';

SpecialBuyBanner.propTypes = {
  specialBuyType: oneOf(['sbotd', 'decor', 'pro']),
};

SpecialBuyBanner.defaultProps = {
  specialBuyType: 'sbotd',
};

export { SpecialBuyBanner };
